import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import userService, { type SignInReq } from "@/api/services/userService";

import { toast } from "sonner";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";
import { getFirstPermittedPath } from "@/constants/permissions";

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	eventToken: string;
	permissions: string[] | null;
	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		setEventToken: (eventToken: string) => void;
		setPermissions: (permissions: string[] | null) => void;
		clearUserInfoAndToken: () => void;
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set) => ({
			userInfo: {},
			userToken: {},
			eventToken: "",
			permissions: null,
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				setEventToken: (eventToken) => {
					set({ eventToken });
				},
				setPermissions: (permissions) => {
					set({ permissions });
				},
				clearUserInfoAndToken() {
					set({ userInfo: {}, userToken: {}, eventToken: "", permissions: null });
				},
			},
		}),
		{
			name: "userStore",
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
				[StorageEnum.EventToken]: state.eventToken,
				[StorageEnum.Permissions]: state.permissions,
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useEventToken = () => useUserStore((state) => state.eventToken);
export const usePermissions = () => useUserStore((state) => state.permissions);
export const useUserPermission = () => useUserStore((state) => state.userInfo.permissions);
export const useUserActions = () => useUserStore((state) => state.actions);

export const useHasPermission = (key: string): boolean => {
	const permissions = usePermissions();
	return permissions?.includes(key) ?? false;
};

export const useHasAnyPermission = (keys: string[]): boolean => {
	const permissions = usePermissions();
	return permissions ? keys.some((key) => permissions.includes(key)) : false;
};

export const useSignIn = () => {
	const navigate = useNavigate();
	const { setUserToken, setUserInfo, setPermissions, clearUserInfoAndToken } = useUserActions();
	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			const { token } = res;
			setUserToken({ token });
			setUserInfo({
				id: "1",
				username: data.loginName,
			});

			const perms = await userService.getPermissions();
			setPermissions(perms);

			const homepage = getFirstPermittedPath(perms);
			navigate(homepage, { replace: true });
			toast.success("Sign in success!", { closeButton: true });
		} catch (err) {
			clearUserInfoAndToken();
			toast.error((err as Error).message, { position: "top-center" });
		}
	};

	return signIn;
};

export default useUserStore;
